import { APICompsSchema } from "@unit/apis";
import { MessageType, ROOM_PATH, roomConverter, RoomType } from "@unit/firestore";
import { doc, setDoc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useAtom } from "jotai";

import { db } from "@/global-state/firebase-settings";
import { authUserAtom, loadingAtom } from "@/global-state/jotai-atom";

export const useRoom = () => {
  const [authUser] = useAtom(authUserAtom);
  const [, setLoading] = useAtom(loadingAtom);

  const addRoom = async (room: RoomType) => {
    setLoading(true);
    try {
      const docRef = doc(db, ROOM_PATH, room.influencerTicketId).withConverter(roomConverter);
      await setDoc(docRef, room);
    } finally {
      setLoading(false);
    }
  };

  const getRoomById = async (influencerTicketId: string): Promise<RoomType | null> => {
    setLoading(true);
    try {
      const docRef = doc(db, `/${ROOM_PATH}`, influencerTicketId).withConverter(roomConverter);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) return docSnap.data();
      else return null;
    } finally {
      setLoading(false);
    }
  };

  const updateRoomLatestMessage = async ({
    influencerTicketId,
    latestMessage,
  }: {
    influencerTicketId: string;
    latestMessage?: MessageType;
  }): Promise<void> => {
    setLoading(true);
    try {
      const docRef = doc(db, `/${ROOM_PATH}`, influencerTicketId).withConverter(roomConverter);
      const room = await getRoomById(influencerTicketId);
      if (!room) return;
      if (latestMessage) {
        let latestMessageData = latestMessage.message.data || "";
        if (latestMessage.message.type === "file") {
          latestMessageData = !!latestMessage.deletedAt
            ? "送信したファイルを削除しました"
            : "PDFファイルを送信しました";
        } else if (latestMessage.message.type === "imageUrl") {
          latestMessageData = !!latestMessage.deletedAt ? "送信した画像を削除しました" : "画像を送信しました";
        }
        await setDoc(docRef, {
          ...room,
          latestMessage: latestMessageData,
          influencerSentTime: latestMessage.senderType === "influencer" ? latestMessage.createTime : 0,
          adminSentTime: latestMessage.senderType === "admin" ? latestMessage.createTime : 0,
          storeSentTime: latestMessage.senderType === "store" ? latestMessage.createTime : 0,
        });
      } else {
        await setDoc(docRef, {
          ...room,
          latestMessage: "",
          influencerSentTime: 0,
          adminSentTime: 0,
          storeSentTime: 0,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getUnreadInfluencerTicketIdList = async (): Promise<APICompsSchema["UUIDValue"][]> => {
    setLoading(true);
    try {
      const collRef = collection(db, `/${ROOM_PATH}`).withConverter(roomConverter);
      const q = query(collRef, where("influencerId", "==", authUser?.id), where("storeSentTime", "!=", null));
      const snapshot = await getDocs(q);
      return snapshot.docs
        .filter((doc) => {
          const storeSentTime = doc.data().storeSentTime;
          const influencerViewedTime = doc.data().influencerViewedTime;
          if (!storeSentTime) {
            return;
          }
          if (influencerViewedTime) {
            return influencerViewedTime <= storeSentTime;
          }
          return doc.data().latestMessage !== "";
        })
        .map((doc) => doc.data().influencerTicketId);
    } finally {
      setLoading(false);
    }
  };

  return {
    addRoom,
    getRoomById,
    getUnreadInfluencerTicketIdList,
    updateRoomLatestMessage,
  };
};
